import {FC} from 'react';
import {Box} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {AiAgentChartData, AiAgentPdfExport, AiAgentTableData} from 'src/types';
import {ChatChart} from './ChatChart';
import {ChatTable} from './ChatTable';
import {CustomTable} from './CustomTable';
import {PdfExportButton} from './PdfExportButton';
import {ExportMessage} from '../utils/collectExportContent';
import {messageSx} from './ChatMessage.sx';

interface ChatMessageProps {
    role: 'user' | 'assistant';
    content: string;
    isStreaming?: boolean;
    charts?: AiAgentChartData[];
    tables?: AiAgentTableData[];
    exports?: AiAgentPdfExport[];
    // Full chat state — a PdfExportButton resolves its content (last table, all
    // tables, charts, everything) from every message, not just this bubble.
    allMessages?: ExportMessage[];
    isError?: boolean;
}

export const ChatMessage: FC<ChatMessageProps> = ({role, content, isStreaming, charts, tables, exports, allMessages, isError}) => {
    if (role === 'user') {
        return (
            <Box sx={messageSx.userRow}>
                <Box sx={messageSx.userBubble}>{content}</Box>
            </Box>
        );
    }

    return (
        <Box sx={messageSx.assistantRow}>
            <Box sx={messageSx.avatar}>
                <SmartToyOutlinedIcon fontSize="inherit" />
            </Box>
            <Box sx={isError ? {...messageSx.assistantBubble, color: 'error.main'} : messageSx.assistantBubble}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content}
                </ReactMarkdown>
                {isStreaming && (
                    <Box component="span" sx={messageSx.typingDots}>
                        <span />
                        <span />
                        <span />
                    </Box>
                )}
                {charts?.map((chart, idx) => (
                    // Key by title (identity) + idx (uniqueness for repeated titles) so a
                    // chart keeps its React instance when siblings are appended around it.
                    <ChatChart key={`chart-${idx}-${chart.title ?? ''}`} data={chart} />
                ))}
                {tables?.map((table, idx) => {
                    const key = `table-${idx}-${(table as {title?: string}).title ?? ''}`;
                    return table.type === 'custom'
                        ? <CustomTable key={key} data={table} />
                        : <ChatTable key={key} data={table} />;
                })}
                {exports?.map((exp, idx) => (
                    <PdfExportButton key={`export-${idx}-${exp.scope}`} export={exp} messages={allMessages ?? []} />
                ))}
            </Box>
        </Box>
    );
};
